import React, { useMemo, useCallback, useState } from 'react'
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
  LabelList,
} from 'recharts'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import EmptyScreen from 'packages/ui/EmptyScreen'
import { BarChartSkeleton } from 'components/global/CUK/Skeletons/BarChartSkeleton'
import { TimeRange } from 'models'

import useUtils from '../use-utils'
import TooltipGraph from '../components/TooltipGraph'
import Legend from '../components/Legend'
import useSelection, { useHoveredItem } from '../store'
import useGraph from '../SectionSsids/use-graph'
import { renderTotalLabel } from '../../../helpers'

interface SSIDConsumptionProps {
  modalTimeRange?: TimeRange
  hasLegend: boolean
}

const GraphConsumption: React.FC<SSIDConsumptionProps> = ({ modalTimeRange, hasLegend = false }) => {
  const { t, i18n } = useTranslation()
  const { customTickFormatter, customDataUsageTickFormatter } = useUtils()
  const [selectedItems] = useSelection()
  const [hoveredItem] = useHoveredItem()
  const [, setHoveredIndex] = useState<number | null>(null)
  
  const { loading: trafficLoading, graph: trafficGraph } = useGraph({ modalTimeRange })

  const chartData = useMemo(() => {
    if (!selectedItems.length) {
      return []
    }
    
    return trafficGraph.map(({ date, ssids, is_estimated }) => {
      const result = {
        timestamp: moment(date).unix() * 1000,
        is_estimated,
        total: 0
      }

      let totalConsumption = 0
      selectedItems.forEach((checkedSSID) => {
        const ssidData = ssids.find(ssid => 
          String(ssid.name || '-') === String(checkedSSID.name || '-') && 
          ssid.network_name === checkedSSID.network_name
        )
        const key = `${checkedSSID.name || '-'}_${checkedSSID.network_name}`
        const value = ssidData ? ssidData.upload + ssidData.download : 0
        result[key] = value
        totalConsumption += value
      })

      result.total = totalConsumption

      return result
    })
  }, [modalTimeRange, trafficGraph, selectedItems])

  const hasData = useMemo(() => {
    if (!selectedItems.length || !chartData.length) return false
    
    return chartData.some(entry => {
      return selectedItems.some(ssid => {
        const key = `${ssid.name || '-'}_${ssid.network_name}`
        return entry[key] > 0
      })
    })
  }, [chartData, selectedItems])

  const legendItems = useMemo(() => {
    return selectedItems.map(item => ({
      label: item.name,
      color: item.color
    })) || []
  }, [selectedItems])

  const getItemOpacity = useCallback((ssidIndex: number) => {
    if (hoveredItem) {
      const ssid = selectedItems[ssidIndex]
      const isItemHovered = String(hoveredItem.name || '-') === String(ssid.name || '-') && 
                           hoveredItem.network_name === ssid.network_name
      return isItemHovered ? 1 : 0.3
    }
    return 1
  }, [hoveredItem, selectedItems])

  const handleMouseMove = useCallback((data) => {
    if (data && data.activeTooltipIndex !== undefined) {
      setHoveredIndex(data.activeTooltipIndex)
    }
  }, [])

  if (trafficLoading) {
    return <BarChartSkeleton />
  }

  if (!selectedItems.length || !hasData) {
    return (
      <Box sx={{ 
        width: '100%',
        height: '100%',
        border: '1px dashed #D7DAEA',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="14px"
          spacing={1.5}
        >
          {t('stats.traffic.graphs.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  const itemCount = selectedItems.filter(item => item.network_name !== 'other').length
  const tooltipPosition = modalTimeRange
    ? { y: -170 }
    : itemCount < 7 ? { y: -(itemCount * 40 + 70) } : { y: -480 }

  return (
    <Stack gap={1} height={'100%'}>
      <ResponsiveContainer>
        <BarChart
          width={500}
          height={300}
          data={chartData}
          margin={{
            top: 40,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <XAxis
            dataKey="timestamp"
            tickFormatter={customTickFormatter}
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
          />
          <YAxis
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
            }}
            tickFormatter={customDataUsageTickFormatter}
          />
          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: 'none', zIndex: 9999 }}
            content={<TooltipGraph dataType={'ssid'} />}
            animationDuration={0}
            position={tooltipPosition}
            offset={60}
          />

          {selectedItems.map((ssid, ssidIndex) => {
            const key = `${ssid.name || '-'}_${ssid.network_name}`
            const isTopElement = ssidIndex === selectedItems.length - 1

            let radius: [number, number, number, number] = [0, 0, 0, 0]
            if (isTopElement) {
              radius = [4, 4, 0, 0]
            }

            return (
              <Bar
                key={key}
                dataKey={key}
                stackId="a"
                fill={ssid.color}
                radius={radius}
                isAnimationActive={false}
              >
                {isTopElement && (
                  <LabelList
                    content={renderTotalLabel(chartData, i18n.language)}
                  />
                )}
                {chartData.map((entry, entryIndex) => (
                  <Cell
                    key={`cell-${key}-${entryIndex}`}
                    fillOpacity={getItemOpacity(ssidIndex)}
                    stroke={entry.is_estimated ? ssid.color : 'none'}
                    strokeWidth={entry.is_estimated ? 1 : 0}
                    strokeDasharray={entry.is_estimated ? '3 3' : '0 0'}
                  />
                ))}
              </Bar>
            )
          })}
        </BarChart>
      </ResponsiveContainer>
      {
        hasLegend && <Legend items={legendItems} />
      }
    </Stack>
  )
}

export default GraphConsumption
